import { formatAmount, formatPercent } from "../../helpers/quickbooks"
import { LoaderSpinner } from "../LoaderSpinner"
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon } from "@heroicons/react/24/outline";
import {
    HIGHER_IS_BETTER,
    isVarianceImprovement,
} from "../../helpers/profitAndLoss/varianceDirection";

export const CardTotal = ({
    data,
    Icon,
    percentageLabel,
    withoutTransform,
    isLoading,
    varianceDirection = HIGHER_IS_BETTER
}) => {

    const hasCompareData =
        data?.baseAmount !== undefined ||
        data?.comparisonAmount !== undefined ||
        data?.varianceAmount !== undefined;

    const showYoYGrowth = !hasCompareData && data?.yoyGrowth !== undefined && data?.yoyGrowth !== null;
    const showPercentage = data?.percentage !== 0 && data?.percentage !== undefined;
    const yoyValue = Number(data?.yoyGrowth);
    const varianceValue = Number(data?.varianceAmount);
    const trendValue = hasCompareData ? varianceValue : yoyValue;
    const trendIsPositive = isVarianceImprovement(trendValue, varianceDirection);
    const showTrend = hasCompareData || showYoYGrowth;
    const TrendIcon = showTrend
        ? (trendIsPositive ? ArrowTrendingUpIcon : ArrowTrendingDownIcon)
        : (Icon || ArrowTrendingUpIcon);
    const trendClassName = trendIsPositive ? "text-emerald-600" : "text-red-500";

    const getVarianceColorClass = (numericValue) => {
        return isVarianceImprovement(numericValue, varianceDirection)
            ? "text-emerald-600"
            : "text-red-500";
    };

    const formatYoYSafe = (value) => {
        return formatPercent(value, false);
    };

    const formatYoYDisplay = (value) => {
        const formatted = formatYoYSafe(value);
        return Number(value) > 0 && formatted !== "-" ? `+${formatted}` : formatted;
    };

    const formatAmountSafe = (value) => {
        if (value === null || value === undefined) return "-";
        return formatAmount(value, withoutTransform);
    };

    const formatPercentSafe = (value) => {
        if (value === null || value === undefined) return "-";
        return formatPercent(value, false);
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white text-card-foreground shadow-sm relative p-3 sm:p-4 w-full min-h-[100px]">
            {isLoading ? (
                <div className="w-full h-full">
                    <LoaderSpinner colorSpinner="black" width="40" height="40" border="3" />
                </div>
            ) : (
                <>
                    <div className="flex flex-row justify-between items-center gap-2">
                        <h6 className="min-w-0 break-words text-xs font-semibold uppercase tracking-wide text-zinc-500">{data?.title}</h6>
                        <TrendIcon className={`h-4 w-4 ${showTrend ? trendClassName : "text-zinc-400"}`} />
                    </div>
                    <div className={`flex flex-row ${hasCompareData ? "border-b border-gray-200" : ""} pb-1`}>
                        <span className="text-xl sm:text-2xl font-bold text-stone-900 tabular-nums">{formatAmount(data?.amount, withoutTransform)}</span>
                    </div>
                    {hasCompareData ? (
                        <div className="mt-1 text-sm">
                            <div className="space-y-1">
                                <div>
                                    <div className="text-sm text-zinc-500">{data?.baseYear || ""}</div>
                                    <div className="text-sm font-bold text-stone-900 tabular-nums">{formatAmountSafe(data?.baseAmount)}</div>
                                </div>

                                <div>
                                    <div className="text-sm text-zinc-500">{data?.comparisonYear || ""}</div>
                                    <div className="text-sm font-bold text-stone-900 tabular-nums">{formatAmountSafe(data?.comparisonAmount)}</div>
                                </div>

                                <div>
                                    <div className="text-sm text-zinc-500">Variance</div>
                                    <div className={`text-sm font-bold tabular-nums ${getVarianceColorClass(varianceValue)}`}>
                                        {formatAmountSafe(data?.varianceAmount)}{" "}
                                        <span className={`font-semibold ${getVarianceColorClass(Number(data?.variancePctChange))}`}>
                                            ({formatPercentSafe(data?.variancePctChange)})
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {(data?.basePercentage !== undefined ||
                                data?.comparisonPercentage !== undefined ||
                                data?.variancePercentage !== undefined) && (
                                <div className="mt-2 border-t border-gray-200 pt-1">
                                    <div className="text-sm font-semibold text-zinc-500">
                                        <div className="mb-1">{percentageLabel || ""}</div>

                                        <div className="space-y-1">
                                            <div>
                                                <div className="text-sm font-normal text-zinc-500">{data?.baseYear || ""}</div>
                                                <div className="tabular-nums text-blue-600">{formatPercentSafe(data?.basePercentage)}</div>
                                            </div>

                                            <div>
                                                <div className="text-sm font-normal text-zinc-500">{data?.comparisonYear || ""}</div>
                                                <div className="tabular-nums text-blue-600">{formatPercentSafe(data?.comparisonPercentage)}</div>
                                            </div>

                                            <div>
                                                <div className="text-sm font-normal text-zinc-500">Variance</div>
                                                <div className={`tabular-nums ${getVarianceColorClass(Number(data?.variancePercentage))}`}>
                                                    {formatPercentSafe(data?.variancePercentage)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        showPercentage && (
                            <div className="text-sm text-zinc-500 tabular-nums">{percentageLabel || ""}{formatPercent(data?.percentage, withoutTransform, true)}</div>
                        )
                    )}
                    {showYoYGrowth && (
                        <div className={`text-sm font-medium tabular-nums ${trendClassName}`}>
                            YoY Growth: {formatYoYDisplay(data?.yoyGrowth)}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}